// Shared "is this tab worth polling right now?" tracker (2026-09-28 incident: an admin tab
// left open in a hidden/background window polled the API every 5s for 16+ hours and read
// 182M D1 rows in a day). The owner's rule is blanket: no page polls while its tab is hidden
// or idle, in every app, enforced rather than fixed one call site at a time.
//
// A tab is ACTIVE when it's visible AND the user has done something in the last
// `idleAfterMs`. `installIdlePause` wires that fact into TanStack Query's `focusManager`, so
// every `refetchInterval` query in the app already pauses/resumes for free (see the
// `focusManager.isFocused()` note below). Code with its OWN timer (a hand-rolled
// `setInterval` that hits the API) reads `isTabActive()` directly.

/** Default idle threshold: no user activity for this long, while visible, still goes idle. */
export const IDLE_AFTER_MS = 10 * 60 * 1000;

// window-level activity signals. Deliberately broad (pointer, keyboard, wheel, touch,
// scroll) so any real interaction resets the deadline, not just clicks.
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;

// Ignore activity bookkeeping for events within this long of the last one — a stream of
// `pointermove`/`scroll` fires far more often than the idle deadline needs to move, so this
// keeps the common case (mouse resting, occasional move) cheap. The event still counts as
// activity; only the re-run of the state machine is throttled.
const ACTIVITY_THROTTLE_MS = 1000;

type ActivityListener = (active: boolean) => void;

let idleAfterMs = IDLE_AFTER_MS;
let active = true;
let lastActivityAt = Date.now();
let idleTimer: ReturnType<typeof setTimeout> | undefined;
let domListenersInstalled = false;
const listeners = new Set<ActivityListener>();

function isHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function setActive(next: boolean): void {
  if (next === active) return;
  active = next;
  for (const listener of listeners) listener(active);
}

function clearIdleTimer(): void {
  if (idleTimer !== undefined) {
    clearTimeout(idleTimer);
    idleTimer = undefined;
  }
}

// ONE rescheduled deadline timer, not a polling interval: fires once, at the moment the tab
// would go idle given the current `lastActivityAt`, then gets cleared/rescheduled by the next
// activity event or visibility change.
function scheduleIdleTimer(): void {
  clearIdleTimer();
  if (isHidden()) return; // already inactive; nothing to schedule
  const remaining = Math.max(idleAfterMs - (Date.now() - lastActivityAt), 0);
  idleTimer = setTimeout(() => setActive(false), remaining);
}

function handleActivityEvent(): void {
  const now = Date.now();
  if (now - lastActivityAt < ACTIVITY_THROTTLE_MS) return; // throttled: still active, just no work
  lastActivityAt = now;
  if (isHidden()) return; // a hidden tab stays inactive regardless of synthetic DOM events
  setActive(true);
  scheduleIdleTimer();
}

function handleVisibilityChange(): void {
  if (isHidden()) {
    clearIdleTimer();
    setActive(false); // hidden -> inactive immediately, no grace period
  } else {
    lastActivityAt = Date.now();
    setActive(true); // becoming visible counts as activity
    scheduleIdleTimer();
  }
}

/**
 * Install the module-level DOM listeners + idle timer (idempotent: safe to call more than
 * once). Returns a teardown that removes every listener this call installed and clears the
 * timer. `active` / `lastActivityAt` are re-baselined to "now" on every install, so a
 * reinstall (e.g. `installIdlePause` called again) starts a fresh idle window rather than
 * inheriting a stale one.
 */
function installTracker(customIdleAfterMs?: number): () => void {
  idleAfterMs = customIdleAfterMs ?? IDLE_AFTER_MS;
  lastActivityAt = Date.now();
  active = !isHidden();

  if (!domListenersInstalled && typeof window !== "undefined") {
    domListenersInstalled = true;
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, handleActivityEvent, { passive: true, capture: true });
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
  }
  scheduleIdleTimer();

  return () => {
    clearIdleTimer();
    if (domListenersInstalled) {
      domListenersInstalled = false;
      for (const type of ACTIVITY_EVENTS) {
        window.removeEventListener(type, handleActivityEvent, { capture: true });
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    }
  };
}

/** Is the tab active right now (visible AND recently interacted with)? */
export function isTabActive(): boolean {
  return active;
}

/**
 * Subscribe to active/inactive transitions. Returns an unsubscribe function. For code that
 * runs its own timer and wants to gate ticks on tab activity directly, rather than going
 * through `installIdlePause` + TanStack Query.
 */
export function onTabActivityChange(cb: ActivityListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * Structural type for TanStack Query's `focusManager` — kept local (not imported from
 * `@tanstack/query-core`) so this package has no dependency on `@tanstack/*`.
 */
export type FocusManagerLike = {
  setEventListener: (setup: (setFocused: (focused?: boolean) => void) => () => void) => void;
};

/**
 * Wire this tab's activity state into TanStack Query's `focusManager`, so
 * `focusManager.isFocused()` reports it instead of the raw `document.visibilityState`.
 *
 * Why this works (verified in `@tanstack/query-core@5.102.8`, `src/queryObserver.ts`
 * ~line 425): a `refetchInterval` tick only fetches when `refetchIntervalInBackground ||
 * focusManager.isFocused()`. So once this is installed, every interval-based query in the
 * app pauses when the tab is hidden or idle, and resumes on the next activity (queries with
 * `refetchOnWindowFocus` also refetch stale data right then, which is the nice UX bonus).
 *
 * Safe to call more than once: TanStack's `setEventListener` runs the PREVIOUS setup's
 * cleanup before invoking the new one, and the cleanup returned here removes every DOM
 * listener this call installed plus the idle timer, so a second call cleanly reinstalls
 * rather than leaking listeners.
 */
export function installIdlePause(
  focusManager: FocusManagerLike,
  opts?: { idleAfterMs?: number }
): void {
  focusManager.setEventListener((setFocused) => {
    const teardownTracker = installTracker(opts?.idleAfterMs);
    const unsubscribe = onTabActivityChange(setFocused);
    setFocused(isTabActive()); // sync TanStack's state immediately, an explicit boolean
    return () => {
      unsubscribe();
      teardownTracker();
    };
  });
}
